import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export const VENDOR_TAB_KEYS = {
  CREDENTIALS: 'configuration-detail',
  OVERVIEW: 'integration-overview',
} as const;

export type VendorTabKey = (typeof VENDOR_TAB_KEYS)[keyof typeof VENDOR_TAB_KEYS];

const TABS: { key: VendorTabKey; label: string }[] = [
  { key: VENDOR_TAB_KEYS.CREDENTIALS, label: 'Integration Settings' },
  { key: VENDOR_TAB_KEYS.OVERVIEW, label: 'Integration Overview' },
];

interface VendorTabNavProps {
  selectedTab: VendorTabKey;
  onSelectTab: (key: VendorTabKey) => void;
  className?: string;
  /** Right-aligned controls in the same row (Edit Settings / Cancel, spec §5.2). */
  trailing?: ReactNode;
}

/**
 * The two-tab nav (Credentials / Overview) shared by all three vendor
 * configuration pages. The tab keys + labels are vendor-agnostic.
 */
export const VendorTabNav = ({ selectedTab, onSelectTab, className, trailing }: VendorTabNavProps) => (
  <div className={cn('flex flex-wrap items-end border-b border-border', className)}>
    {TABS.map((tab) => (
      <button
        key={tab.key}
        type="button"
        onClick={() => onSelectTab(tab.key)}
        className={cn(
          'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
          selectedTab === tab.key
            ? 'border-primary text-primary'
            : 'border-transparent text-muted-foreground hover:text-foreground',
        )}
      >
        {tab.label}
      </button>
    ))}
    {/* `self-center` opts out of the row's `items-end`, then the controls are
        lifted 10px — the prototype's own `position:relative; top:-10px`. Centred
        in the 39px row alone they sit 23px below the card's inner top edge and
        only 4px above the divider, which reads as pinned to the rule; the lift
        balances that to 13px / 14px, as the design has it. `relative` shifts
        paint only, so the row height and the divider stay put.

        Unconditional, as in the prototype: when the row wraps at phone width the
        controls drop to their own line and still clear the tab labels by ~28px,
        so the lift needs no breakpoint guard. */}
    {trailing && (
      <div className="ml-auto flex items-center gap-2 self-center relative -top-2.5">
        {trailing}
      </div>
    )}
  </div>
);
